import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Clock, Repeat, Gem } from 'lucide-react';
import { SectionTitle, FlowerMotif } from './premium/Decor';

const REASONS = [
  {
    icon: Clock,
    hi: 'सही समय और सटीक मुहूर्त',
    en: 'Right Time & Perfect Timing',
    dHi: 'जानें कब कार्य करना है — चाहे वो विवाह हो, करियर का अवसर, या कोई महत्वपूर्ण जीवन निर्णय। सही समय के साथ अपने कार्यों को संरेखित करें ताकि अधिकतम परिणाम मिलें।',
    dEn: 'Know when to act for the best outcomes — whether it\'s marriage, career opportunities, or key life decisions. Align your actions with the right timing to maximise results.',
  },
  {
    icon: Repeat,
    hi: 'असफलताओं के चक्र को तोड़ें',
    en: 'Break the Cycle of Setbacks',
    dHi: 'बार-बार होने वाली असफलताओं के असली कारणों को समझें। अपने ग्रह योगों के साथ काम करना सीखें और स्पष्टता तथा आत्मविश्वास के साथ आगे बढ़ें।',
    dEn: 'Understand the real reasons behind repeated failures. Learn how to work with your planetary patterns and move forward with clarity and confidence.',
  },
  {
    icon: Gem,
    hi: 'सही रत्न, सही उपाय',
    en: 'Right Gemstone, Right Remedies',
    dHi: 'अनुमान न लगाएं। अपनी जन्म कुंडली से जानें कि आपके लिए क्या वाकई काम करता है — रत्नों से लेकर व्यावहारिक उपायों तक।',
    dEn: 'Avoid guesswork. Discover what truly works for you through your birth chart — from gemstones to practical remedies.',
  },
];

const WhyShouldYouSection = () => {
  const { language } = useLanguage();
  const isHi = language === 'hi';

  return (
    <section
      data-testid="why-should-you-section"
      className="relative bg-brand-cream py-16 md:py-24 overflow-hidden"
    >
      <div className="absolute inset-0 bg-mandala-watermark bg-repeat opacity-50 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitle
          eyebrow={isHi ? 'आपके लाभ' : 'Your Benefits'}
          title={isHi ? 'आपको प्रीमियम कुंडली क्यों बनवानी चाहिए?' : 'Why Should You Get Your Premium Personalized Kundli Made?'}
          subtitle={isHi
            ? 'सिर्फ़ जानकारी नहीं — एक स्पष्ट मार्गदर्शन जो आपके जीवन को बदल दे।'
            : 'Not just information — a clear guidance that transforms your life.'}
          language={language}
        />

        <div className="mt-14 grid md:grid-cols-3 gap-6 md:gap-8">
          {REASONS.map((r, i) => {
            const Icon = r.icon;
            return (
              <div
                key={i}
                className="group relative bg-white rounded-3xl border-2 border-brand-gold/25 p-8 md:p-10 hover:border-brand-gold hover:shadow-premium hover:-translate-y-2 transition-all duration-300 overflow-hidden opacity-0 animate-fade-up"
                style={{ animationDelay: `${i * 100}ms` }}
              >
                {/* Decorative gradient blob */}
                <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-brand-gold/12 blur-3xl group-hover:bg-brand-gold/25 transition-colors" />
                <div className="absolute bottom-3 right-3 opacity-25 group-hover:opacity-50 transition-opacity">
                  <FlowerMotif className="w-12 h-12 text-brand-gold" />
                </div>

                <div className="relative">
                  {/* Icon block */}
                  <div className="relative w-20 h-20 mb-6">
                    <div className="absolute inset-0 bg-brand-gold/30 rounded-full blur-xl" />
                    <div className="relative w-full h-full rounded-2xl bg-gradient-to-br from-brand-maroon via-brand-maroon-light to-brand-maroon-dark text-brand-gold-light flex items-center justify-center shadow-maroon-soft">
                      <Icon className="w-10 h-10" />
                    </div>
                  </div>

                  <h3 className={`font-playfair font-bold text-2xl text-brand-maroon mb-4 leading-tight ${isHi ? 'font-hindi-serif' : ''}`}>
                    {isHi ? r.hi : r.en}
                  </h3>
                  <p className={`text-brand-text-soft text-base leading-relaxed ${isHi ? 'font-hindi' : 'font-outfit'}`}>
                    {isHi ? r.dHi : r.dEn}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhyShouldYouSection;
